import type { Office } from "./types"

export const BRISTOL_SPECIALISMS = [
  "Residential Sales",
  "Lettings",
  "Property Management",
  "New Homes",
  "Commercial",
  "Land & Development",
  "Auctions",
  "Relocation",
] as const

export const BRISTOL_AREAS = [
  "Clifton",
  "Clifton Village",
  "Redland",
  "Cotham",
  "Bishopston",
  "Stoke Bishop",
  "Sneyd Park",
  "Westbury-on-Trym",
  "Henleaze",
  "Southville",
  "Bedminster",
  "Harbourside",
  "City Centre",
  "Whiteladies Road",
  "Leigh Woods",
  "Long Ashton",
  "Abbots Leigh",
] as const

export const BRISTOL_OFFICES: Office[] = [
  {
    slug: "savills-clifton",
    name: "Savills",
    branch: "Clifton",
    address: "20 The Mall, Clifton",
    postcode: "BS8 4DR",
    phone: "0117 295 7573",
    website: "https://www.savills.co.uk/estate-agents/south-west-england/savills-bristol-clifton.aspx",
    email: "clifton@savills.com",
    description:
      "Savills Clifton has been operating in the region for nearly 20 years and is Bristol's leading international property agent. Their team has over 75 years of combined property knowledge, specialising in period properties, townhouses, and country homes across Clifton, South Gloucestershire, and North Somerset.",
    established: 1855,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Land & Development"],
    areas: ["Clifton", "Clifton Village", "Leigh Woods", "Sneyd Park", "Stoke Bishop", "Long Ashton", "Abbots Leigh"],
    propertyCount: 85,
    avgPrice: "£750,000",
    rating: 4.6,
    reviewCount: 142,
    hours: [
      { days: "Mon–Fri", time: "08:30–18:00" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Founded in 1855, Savills is one of the world's most recognised property brands, and their Clifton office on The Mall brings that heritage directly to Bristol's premier residential neighbourhood. The team combines over 75 years of collective experience, with particular strength in period properties, Georgian townhouses, and country homes.\n\nSavills Clifton serves a discerning client base across BS8 and into North Somerset, from first-time buyers in Clifton Village apartments to families seeking substantial homes in Sneyd Park and Leigh Woods. Their international network means access to a global buyer pool, while the local knowledge runs deep.\n\nWith an average listing price of £750,000 and 85 properties typically on their books, this is an office pitched firmly at the upper end of Bristol's market.",
    whyChoose: [
      "Over 165 years of estate agency heritage",
      "Rated 4.6/5 from 142 client reviews",
      "Specialists in period properties across Clifton and North Somerset",
      "International buyer network with genuine local expertise",
    ],
    faq: [
      {
        question: "What areas does Savills Clifton cover?",
        answer:
          "Savills Clifton covers Clifton, Clifton Village, Leigh Woods, Sneyd Park, Stoke Bishop, Long Ashton, and Abbots Leigh, with a particular focus on the BS8 postcode and surrounding North Somerset villages.",
      },
      {
        question: "What services does Savills offer in Clifton?",
        answer:
          "Savills Clifton provides residential sales, lettings, new homes, and land and development services. Their team specialises in period properties, townhouses, and country homes.",
      },
      {
        question: "What are Savills Clifton's opening hours?",
        answer:
          "Savills Clifton is open Monday to Friday from 08:30 to 18:00 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "knight-frank-clifton",
    name: "Knight Frank",
    branch: "Clifton",
    address: "1 The Mall, Clifton",
    postcode: "BS8 4DP",
    phone: "0117 317 1999",
    website: "https://www.knightfrank.co.uk/offices/bristol-estate-agents",
    email: "bristol@knightfrank.com",
    description:
      "Knight Frank's Bristol office, situated in the heart of Clifton, handles some of the city's finest residential properties. With over 125 years redefining estate agency standards, they advise on sales, lettings, and property valuations across the wider Bristol and North Somerset area. Rated 4.8 stars based on client reviews.",
    established: 1896,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Land & Development", "Relocation"],
    areas: ["Clifton", "Clifton Village", "Leigh Woods", "Sneyd Park", "Long Ashton", "Abbots Leigh"],
    propertyCount: 65,
    avgPrice: "£925,000",
    rating: 4.8,
    reviewCount: 79,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Knight Frank has been redefining expectations in property since 1896, and their Clifton office is no exception. Positioned at 1 The Mall, they handle Bristol's most prestigious residential instructions, with an average listing price approaching £925,000. This is where the city's prime market comes to transact.\n\nThe team advises across sales, lettings, new homes, land, and relocation, offering a breadth of service that few local agents can match. Their particular strength lies in connecting Bristol sellers with an international network of qualified buyers, something that makes a measurable difference at the top end of the market.\n\nWith a 4.8-star rating and a reputation built on discretion and results, Knight Frank Clifton is a natural choice for those seeking a premium, full-service experience.",
    whyChoose: [
      "Rated 4.8/5 based on client reviews",
      "Average listing price of £925,000, reflecting prime market focus",
      "Full service: sales, lettings, new homes, land, and relocation",
      "Global buyer network with a genuinely local Bristol team",
    ],
    faq: [
      {
        question: "What areas does Knight Frank Clifton cover?",
        answer:
          "Knight Frank Clifton covers Clifton, Clifton Village, Leigh Woods, Sneyd Park, Long Ashton, and Abbots Leigh, with a wider reach across North Somerset for country properties.",
      },
      {
        question: "What services does Knight Frank offer in Clifton?",
        answer:
          "Knight Frank Clifton offers residential sales, lettings, new homes, land and development, and relocation services. They also provide property valuations and buying advice for premium homes.",
      },
      {
        question: "What are Knight Frank Clifton's opening hours?",
        answer:
          "Knight Frank Clifton is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "hollis-morgan-clifton",
    name: "Hollis Morgan",
    branch: "Clifton",
    address: "49 Alma Vale Road, Clifton",
    postcode: "BS8 2HL",
    phone: "0117 973 6565",
    website: "https://www.hollismorgan.co.uk",
    email: "info@hollismorgan.co.uk",
    description:
      "Hollis Morgan is an independent Clifton estate agent and auctioneer with an excellent reputation for selling and letting properties across the BS8 and BS9 postcodes. With deep local knowledge and a personal approach, they also handle auctions and new homes across the West Country.",
    established: 1994,
    specialisms: ["Residential Sales", "Lettings", "Auctions", "New Homes"],
    areas: ["Clifton", "Clifton Village", "Redland", "Cotham", "Stoke Bishop", "Sneyd Park", "Leigh Woods"],
    propertyCount: 110,
    avgPrice: "£550,000",
    rating: 4.8,
    reviewCount: 287,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:30–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Since 1994, Hollis Morgan has built one of Bristol's strongest independent reputations from their Alma Vale Road office. They are that rare thing: an agent equally comfortable conducting a high-energy auction as they are quietly negotiating the sale of a Georgian townhouse in Clifton Village.\n\nWith 287 client reviews and a 4.8-star rating, the numbers speak for themselves. The team covers a wide patch across BS8 and BS9, from Redland terraces to Sneyd Park family homes, with around 110 properties on their books at any given time. Their auction expertise sets them apart, providing an alternative route to market that many sellers overlook.\n\nHollis Morgan also handles new homes across the West Country, making them a versatile choice for developers and buyers alike.",
    whyChoose: [
      "Rated 4.8/5 from 287 client reviews",
      "Independent agent with specialist auction expertise",
      "110 properties typically available across BS8 and BS9",
      "Deep local knowledge spanning Clifton, Redland, and Stoke Bishop",
    ],
    faq: [
      {
        question: "What areas does Hollis Morgan Clifton cover?",
        answer:
          "Hollis Morgan covers Clifton, Clifton Village, Redland, Cotham, Stoke Bishop, Sneyd Park, and Leigh Woods, primarily across the BS8 and BS9 postcodes.",
      },
      {
        question: "What services does Hollis Morgan offer in Clifton?",
        answer:
          "Hollis Morgan provides residential sales, lettings, property auctions, and new homes services. Their auction department is particularly well established, offering an alternative route to sale for a range of property types.",
      },
      {
        question: "What are Hollis Morgan Clifton's opening hours?",
        answer:
          "Hollis Morgan Clifton is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:30 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "cj-hole-clifton",
    name: "CJ Hole",
    branch: "Clifton",
    address: "161 Whiteladies Road, Clifton",
    postcode: "BS8 2RF",
    phone: "0117 435 1867",
    website: "https://www.cjhole.co.uk/estate-agents-and-letting-agents/branch/clifton/",
    email: "clifton@cjhole.co.uk",
    description:
      "CJ Hole was first established on Park Street in Clifton in 1867, making it one of Bristol's oldest estate agency brands. Their Clifton branch has recently relocated to a prominent corner office between Clifton and Redland, with the team boasting over 75 years of combined experience in the Bristol area.",
    established: 1867,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes", "Land & Development"],
    areas: ["Clifton", "Redland", "Cotham", "Whiteladies Road", "Bishopston", "Stoke Bishop"],
    propertyCount: 145,
    avgPrice: "£425,000",
    rating: 4.3,
    reviewCount: 312,
    hours: [
      { days: "Mon–Fri", time: "08:45–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Few Bristol estate agents can claim roots stretching back to 1867, but CJ Hole is one of them. Originally established on Park Street, the brand has been a fixture of Clifton's property landscape for well over 150 years. Their current branch on Whiteladies Road sits in a prominent corner position that catches foot traffic from both Clifton and Redland.\n\nThe team brings over 75 years of combined experience and handles one of the largest portfolios in the area, with around 145 properties on their books. They serve a broad client base, from young professionals seeking their first Cotham flat to established families looking to upsize in Stoke Bishop.\n\nCJ Hole Clifton offers the full range: sales, lettings, property management, new homes, and land. That breadth, combined with their heritage, makes them a solid all-round choice.",
    whyChoose: [
      "Over 155 years of Bristol estate agency heritage",
      "312 client reviews with a 4.3/5 rating",
      "One of the largest portfolios in the area, with 145 active listings",
      "Full service: sales, lettings, property management, new homes, and land",
    ],
    faq: [
      {
        question: "What areas does CJ Hole Clifton cover?",
        answer:
          "CJ Hole Clifton covers Clifton, Redland, Cotham, Whiteladies Road, Bishopston, and Stoke Bishop. Their prominent position between Clifton and Redland gives them strong visibility across both neighbourhoods.",
      },
      {
        question: "What services does CJ Hole offer in Clifton?",
        answer:
          "CJ Hole Clifton provides residential sales, lettings, property management, new homes, and land and development services. Their team has over 75 years of combined experience in the Bristol property market.",
      },
      {
        question: "What are CJ Hole Clifton's opening hours?",
        answer:
          "CJ Hole Clifton is open Monday to Friday from 08:45 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "ocean-clifton",
    name: "Ocean",
    branch: "Clifton",
    address: "187–189 Whiteladies Road, Clifton",
    postcode: "BS8 2RY",
    phone: "0117 946 6007",
    website: "https://www.oceanhome.co.uk/contact-us/clifton-sales/",
    email: "clifton@oceanhome.co.uk",
    description:
      "Ocean is one of Bristol's largest independent estate agencies, founded in 1983. Their Clifton head office on Whiteladies Road opened in 1990 and offers a full range of residential services. Gold Award winners in the Best Estate Agent Guide, they handle a high volume of sales and lettings across the BS8 area and beyond.",
    established: 1983,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Clifton", "Redland", "Cotham", "Whiteladies Road", "City Centre", "Harbourside"],
    propertyCount: 175,
    avgPrice: "£385,000",
    rating: 4.4,
    reviewCount: 456,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Ocean's Clifton office is the head office of one of Bristol's most recognised independent agencies. Established in 1983, the brand has grown into a multi-branch operation, but this Whiteladies Road location remains the flagship. It opened in 1990 and has been a dominant presence in the BS8 market ever since.\n\nWith 175 active listings and 456 client reviews, Ocean Clifton operates at serious volume. They are Gold Award winners in the Best Estate Agent Guide, a distinction earned through consistent performance across sales, lettings, and property management. The team handles everything from city centre apartments to period homes in Redland and Cotham.\n\nFor sellers and landlords who value reach and momentum, Ocean's scale across Bristol provides a clear advantage. Their buyer database is among the city's largest.",
    whyChoose: [
      "Gold Award winners in the Best Estate Agent Guide",
      "456 client reviews, one of Bristol's most-reviewed agencies",
      "175 active listings across Clifton, Redland, and the city centre",
      "Bristol's largest independent agency, founded in 1983",
    ],
    faq: [
      {
        question: "What areas does Ocean Clifton cover?",
        answer:
          "Ocean Clifton covers Clifton, Redland, Cotham, Whiteladies Road, City Centre, and Harbourside. As Ocean's head office, the team also coordinates across the wider Bristol branch network.",
      },
      {
        question: "What services does Ocean offer in Clifton?",
        answer:
          "Ocean Clifton provides residential sales, lettings, and property management. They are Gold Award winners in the Best Estate Agent Guide and handle one of the highest volumes of property transactions in the BS8 area.",
      },
      {
        question: "What are Ocean Clifton's opening hours?",
        answer:
          "Ocean Clifton is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "chappell-matthews-clifton",
    name: "Chappell & Matthews",
    branch: "Clifton Village",
    address: "4 Boyces Avenue, Clifton Village",
    postcode: "BS8 4AA",
    phone: "0117 973 4402",
    website: "https://www.chappellandmatthews.co.uk",
    email: "clifton@chappellandmatthews.co.uk",
    description:
      "Chappell & Matthews have been experts in the property market since 1913, helping discerning customers with bespoke requirements. Their Clifton Village branch combines over a century of traditional values with modern marketing to achieve strong results across sales, lettings, and property management.",
    established: 1913,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Clifton Village", "Clifton", "Redland", "Cotham", "Sneyd Park", "Stoke Bishop"],
    propertyCount: 95,
    avgPrice: "£520,000",
    rating: 4.5,
    reviewCount: 198,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:30–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Chappell & Matthews have been part of Bristol's property landscape since 1913, making them one of the city's longest-serving independent agencies. Their Clifton Village branch on Boyces Avenue sits in the heart of the conservation area, surrounded by the very properties they know best: Georgian terraces, Victorian villas, and character conversions.\n\nThe team blends traditional values with contemporary marketing, a combination that resonates with the discerning clientele they tend to attract. With 95 properties typically on their books and an average listing price of £520,000, they occupy a confident middle ground between boutique and high-volume.\n\nClients frequently praise the personal attention and honest advice. A 4.5-star rating across 198 reviews reflects the consistency of their approach over more than a century of trading.",
    whyChoose: [
      "Over 110 years of continuous estate agency experience",
      "Rated 4.5/5 from 198 client reviews",
      "Specialists in Clifton Village and surrounding conservation areas",
      "Traditional values paired with modern property marketing",
    ],
    faq: [
      {
        question: "What areas does Chappell & Matthews Clifton Village cover?",
        answer:
          "Chappell & Matthews cover Clifton Village, Clifton, Redland, Cotham, Sneyd Park, and Stoke Bishop. Their office location in the heart of Clifton Village gives them particular strength in the BS8 postcode.",
      },
      {
        question: "What services does Chappell & Matthews offer in Clifton Village?",
        answer:
          "Chappell & Matthews provide residential sales, lettings, and property management. They work with both private sellers and landlords, combining over a century of experience with modern marketing techniques.",
      },
      {
        question: "What are Chappell & Matthews Clifton Village's opening hours?",
        answer:
          "Chappell & Matthews Clifton Village is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:30 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "maggs-allen-clifton",
    name: "Maggs & Allen",
    branch: "Clifton",
    address: "10 Alma Vale Road, Clifton",
    postcode: "BS8 2HY",
    phone: "0117 973 4940",
    website: "https://www.maggsandallen.co.uk",
    email: "info@maggsandallen.co.uk",
    description:
      "Maggs & Allen has been a fixture of the Bristol property market for over 35 years, with offices in Clifton and Henleaze. Their Clifton office is well known for auction sales alongside conventional residential sales, lettings, and chartered surveying. A trusted team of experienced RICS Surveyors and expert Auctioneers.",
    established: 1988,
    specialisms: ["Residential Sales", "Lettings", "Auctions", "Commercial"],
    areas: ["Clifton", "Redland", "Cotham", "City Centre", "Harbourside", "Southville", "Bedminster"],
    propertyCount: 80,
    avgPrice: "£365,000",
    rating: 4.2,
    reviewCount: 175,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–12:30" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Maggs & Allen brings a distinctive combination of skills to the Bristol market. Established in 1988, their Clifton office on Alma Vale Road houses RICS-qualified Chartered Surveyors alongside experienced estate agents and auctioneers. That breadth of expertise under one roof is unusual, and it gives clients access to genuinely informed property advice.\n\nTheir auction department is one of Bristol's busiest, regularly selling properties from Clifton through to Southville and Bedminster. For sellers looking beyond the conventional route, or investors seeking opportunities at speed, this is a proven track record. The conventional sales and lettings team covers a wide spread of areas and price points, with an average listing around £365,000.\n\nWith offices in both Clifton and Henleaze, the firm has good coverage of north and central Bristol, along with a strong presence in the commercial property sector.",
    whyChoose: [
      "RICS-qualified Chartered Surveyors and expert Auctioneers in-house",
      "Over 35 years in the Bristol property market",
      "Specialist auction services alongside conventional sales and lettings",
      "Wide coverage from Clifton through to Southville and Bedminster",
    ],
    faq: [
      {
        question: "What areas does Maggs & Allen Clifton cover?",
        answer:
          "Maggs & Allen Clifton covers Clifton, Redland, Cotham, City Centre, Harbourside, Southville, and Bedminster. They have particularly strong coverage of central Bristol, with a second office in Henleaze for the north of the city.",
      },
      {
        question: "What services does Maggs & Allen offer in Clifton?",
        answer:
          "Maggs & Allen provides residential sales, lettings, property auctions, and commercial property services. Their team includes RICS-qualified Chartered Surveyors and experienced Auctioneers, offering surveying and valuation advice alongside agency services.",
      },
      {
        question: "What are Maggs & Allen Clifton's opening hours?",
        answer:
          "Maggs & Allen Clifton is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 12:30. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "andrews-clifton",
    name: "Andrews",
    branch: "Clifton",
    address: "158 Whiteladies Road, Clifton",
    postcode: "BS8 2XZ",
    phone: "0117 973 2551",
    website: "https://www.andrewsonline.co.uk/branches/clifton",
    email: "clifton@andrewsonline.co.uk",
    description:
      "Andrews began in the property business in 1946 and has been operating in the Clifton area since 1978. Their philosophy is all about making life easier. They are a full-service agency sharing knowledge and expertise across their whole branch network, covering sales, lettings, mortgages, conveyancing, and surveys.",
    established: 1946,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Clifton", "Redland", "Cotham", "Whiteladies Road", "Westbury-on-Trym", "Henleaze"],
    propertyCount: 130,
    avgPrice: "£410,000",
    rating: 4.1,
    reviewCount: 389,
    hours: [
      { days: "Mon–Fri", time: "09:30–18:00" },
      { days: "Sat", time: "09:00–17:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Andrews has been in the property business since 1946 and arrived in Clifton in 1978, making them a firmly established part of the Whiteladies Road scene. Their philosophy centres on making life easier for clients, and they back that up with a genuinely full-service offering: sales, lettings, mortgages, conveyancing, and surveys all available through one team.\n\nWith 130 active listings and 389 client reviews, Andrews Clifton is a high-volume operation. They serve a broad demographic, from students and young professionals looking for rental properties in Cotham to growing families seeking homes in Henleaze and Westbury-on-Trym.\n\nThe branch network effect is a real advantage here. Knowledge and buyer interest flow between Andrews offices, meaning properties get exposure beyond the immediate catchment. Saturday opening until 17:00 is notably generous for the area.",
    whyChoose: [
      "Full-service agency: sales, lettings, mortgages, conveyancing, and surveys",
      "389 client reviews across the Clifton branch",
      "Established in 1946 with nearly 50 years in the Clifton area",
      "Saturday opening until 17:00, one of the longest in the area",
    ],
    faq: [
      {
        question: "What areas does Andrews Clifton cover?",
        answer:
          "Andrews Clifton covers Clifton, Redland, Cotham, Whiteladies Road, Westbury-on-Trym, and Henleaze. Their branch network provides additional reach across the wider Bristol area.",
      },
      {
        question: "What services does Andrews offer in Clifton?",
        answer:
          "Andrews Clifton provides residential sales, lettings, property management, and new homes services. They also offer in-house mortgages, conveyancing, and property surveys, making them a true one-stop shop.",
      },
      {
        question: "What are Andrews Clifton's opening hours?",
        answer:
          "Andrews Clifton is open Monday to Friday from 09:30 to 18:00 and Saturday from 09:00 to 17:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "hamptons-clifton",
    name: "Hamptons",
    branch: "Clifton Village",
    address: "8 The Mall, Clifton Village",
    postcode: "BS8 4DR",
    phone: "0117 911 1455",
    website: "https://www.hamptons.co.uk/estate-agents/clifton",
    email: "clifton@hamptons.co.uk",
    description:
      "Hamptons' Clifton Village office is conveniently located close to the Suspension Bridge, with over 60 years of combined team experience. As part of the Connells Group, they have access to an extensive buyer network while maintaining the personal touch expected of a premium Clifton agent, specialising in sales, lettings, and country properties.",
    established: 1869,
    specialisms: ["Residential Sales", "Lettings", "New Homes", "Relocation"],
    areas: ["Clifton", "Clifton Village", "Redland", "Stoke Bishop", "Leigh Woods", "Long Ashton"],
    propertyCount: 70,
    avgPrice: "£680,000",
    rating: 4.4,
    reviewCount: 121,
    hours: [
      { days: "Mon–Thu", time: "08:45–18:00" },
      { days: "Fri", time: "08:45–17:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Hamptons traces its origins to 1869, and their Clifton Village office on The Mall carries that pedigree into one of Bristol's most sought-after addresses. Situated close to the Clifton Suspension Bridge, the team of experienced agents brings over 60 years of combined knowledge to the local market.\n\nAs part of the Connells Group, Hamptons benefits from an extensive national buyer network while maintaining the personal, premium service expected in Clifton Village. The average listing price of £680,000 reflects their focus on quality homes in desirable locations, from Leigh Woods retreats to Stoke Bishop family houses.\n\nTheir relocation service is a particular strength, supporting clients moving into Bristol from elsewhere in the UK or internationally. With 70 properties typically on their books, the approach is curated rather than high-volume.",
    whyChoose: [
      "Over 150 years of property expertise since 1869",
      "Relocation service for clients moving to Bristol from across the UK",
      "Curated portfolio with an average listing price of £680,000",
      "National buyer network through the Connells Group partnership",
    ],
    faq: [
      {
        question: "What areas does Hamptons Clifton Village cover?",
        answer:
          "Hamptons Clifton Village covers Clifton, Clifton Village, Redland, Stoke Bishop, Leigh Woods, and Long Ashton. They specialise in premium residential areas across the BS8 postcode and into North Somerset.",
      },
      {
        question: "What services does Hamptons offer in Clifton Village?",
        answer:
          "Hamptons Clifton Village provides residential sales, lettings, new homes, and relocation services. They also handle country properties and offer specialist support for clients relocating to Bristol.",
      },
      {
        question: "What are Hamptons Clifton Village's opening hours?",
        answer:
          "Hamptons Clifton Village is open Monday to Thursday from 08:45 to 18:00, Friday from 08:45 to 17:00, and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "goodman-lilley-henleaze",
    name: "Goodman & Lilley",
    branch: "Henleaze",
    address: "156 Henleaze Road, Henleaze",
    postcode: "BS9 4NB",
    phone: "0117 962 3030",
    website: "https://www.goodmanlilley.co.uk",
    email: "sales@goodmanlilley.co.uk",
    description:
      "Goodman & Lilley is a well-regarded independent estate agent with branches across Bristol and North Somerset. Their Henleaze branch covers Clifton, Redland, and the surrounding areas, with a strong reputation for honest valuations, proactive marketing, and a genuinely personal service.",
    established: 2006,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "Land & Development"],
    areas: ["Henleaze", "Westbury-on-Trym", "Stoke Bishop", "Redland", "Clifton", "Bishopston"],
    propertyCount: 90,
    avgPrice: "£445,000",
    rating: 4.6,
    reviewCount: 234,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–14:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Goodman & Lilley launched in 2006 with a simple proposition: honest valuations, proactive marketing, and genuinely personal service. Two decades later, that approach has earned them a 4.6-star rating from 234 reviews and a loyal client base across north Bristol.\n\nTheir Henleaze branch on Henleaze Road is ideally positioned to serve the BS9 postcode and surrounding areas. The team covers everything from Westbury-on-Trym family homes to Bishopston terraces, with 90 properties typically on their books. They also handle land and development work, giving them insight into the new-build market alongside existing stock.\n\nAs an independent agency with multiple branches, Goodman & Lilley offers a useful blend of local commitment and broader reach. Clients appreciate the lack of corporate bureaucracy and the direct access to decision-makers.",
    whyChoose: [
      "Rated 4.6/5 from 234 client reviews",
      "Independent agency with a personal, hands-on approach",
      "Strong presence across Henleaze, Westbury-on-Trym, and Stoke Bishop",
      "Land and development expertise alongside residential sales and lettings",
    ],
    faq: [
      {
        question: "What areas does Goodman & Lilley Henleaze cover?",
        answer:
          "Goodman & Lilley Henleaze covers Henleaze, Westbury-on-Trym, Stoke Bishop, Redland, Clifton, and Bishopston. Their office on Henleaze Road gives them strong coverage of the BS9 postcode in particular.",
      },
      {
        question: "What services does Goodman & Lilley offer in Henleaze?",
        answer:
          "Goodman & Lilley provides residential sales, lettings, property management, and land and development services. They are known for honest valuations and proactive marketing across all property types.",
      },
      {
        question: "What are Goodman & Lilley Henleaze's opening hours?",
        answer:
          "Goodman & Lilley Henleaze is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 14:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "alexander-may-clifton",
    name: "Alexander May",
    branch: "Clifton",
    address: "35 Regent Street, Clifton",
    postcode: "BS8 4HR",
    phone: "0117 974 4766",
    website: "https://alexandermay.co.uk",
    email: "info@alexandermay.co.uk",
    description:
      "Alexander May is a modern, independent estate agent taking a contemporary approach through utilising technology while staying true to honesty and integrity. Strategically positioned in Clifton Village, they cover the whole of Bristol and North Somerset with passionate, forward-thinking service and real results.",
    established: 2012,
    specialisms: ["Residential Sales", "Lettings", "Land & Development", "New Homes"],
    areas: ["Clifton Village", "Clifton", "Leigh Woods", "Sneyd Park", "Stoke Bishop", "Long Ashton"],
    propertyCount: 55,
    avgPrice: "£625,000",
    rating: 4.9,
    reviewCount: 152,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "10:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Alexander May launched in 2012 with a clear ambition: to bring fresh thinking and technology-led marketing to Bristol's premium property market, without sacrificing the integrity that clients rightly expect. Their Regent Street office in Clifton Village puts them at the centre of the action.\n\nThe results speak clearly. A 4.9-star rating from 152 reviews makes them one of Bristol's highest-rated agents, and their average listing price of £625,000 reflects a focus on quality over volume. With 55 carefully selected properties on their books at any time, every instruction gets genuine attention.\n\nAlexander May serves sellers and buyers across Clifton Village, Leigh Woods, Sneyd Park, Stoke Bishop, and Long Ashton. Their land and development work adds another dimension, with experience in new homes and site acquisitions across North Somerset.",
    whyChoose: [
      "Rated 4.9/5, one of Bristol's highest-rated estate agents",
      "Modern, technology-led marketing with a personal touch",
      "Focused portfolio of premium properties averaging £625,000",
      "Independent and owner-managed since 2012",
    ],
    faq: [
      {
        question: "What areas does Alexander May Clifton cover?",
        answer:
          "Alexander May covers Clifton Village, Clifton, Leigh Woods, Sneyd Park, Stoke Bishop, and Long Ashton, with wider reach across Bristol and North Somerset for premium properties.",
      },
      {
        question: "What services does Alexander May offer in Clifton?",
        answer:
          "Alexander May provides residential sales, lettings, land and development, and new homes services. They combine technology-driven marketing with a personal, hands-on approach to achieve strong results.",
      },
      {
        question: "What are Alexander May Clifton's opening hours?",
        answer:
          "Alexander May Clifton is open Monday to Friday from 09:00 to 17:30 and Saturday from 10:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "ocean-southville",
    name: "Ocean",
    branch: "Southville",
    address: "The Plaza, 275 North Street, Southville",
    postcode: "BS3 1JN",
    phone: "0117 923 1866",
    website: "https://www.oceanhome.co.uk/contact-us/southville-sales/",
    email: "southville@oceanhome.co.uk",
    description:
      "Ocean's Southville branch is the go-to agent for BS3, serving Southville, Bedminster, and surrounding areas for over 25 years. Gold Award winners in the Best Estate Agent Guide, they handle a large volume of sales and lettings in one of Bristol's fastest-growing residential markets.",
    established: 1983,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Southville", "Bedminster", "Harbourside", "City Centre"],
    propertyCount: 160,
    avgPrice: "£340,000",
    rating: 4.3,
    reviewCount: 378,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Ocean Southville is the go-to agent for BS3, a postcode that has undergone one of Bristol's most dramatic transformations over the past two decades. From North Street's independent shops to the waterfront developments near Harbourside, this is a neighbourhood on the move, and Ocean has been there throughout.\n\nWith over 25 years serving Southville and Bedminster, the team handles a significant volume of transactions: 160 active listings, 378 client reviews, and a Gold Award in the Best Estate Agent Guide. They know the local market intimately, from Victorian terraces in Southville to new-build apartments in Bedminster.\n\nOcean's 'people before property' ethos means every client gets a dedicated Move Manager. For sellers and landlords in BS3 who want reach, experience, and proven results, this branch is hard to look past.",
    whyChoose: [
      "The go-to agent for BS3 with over 25 years of local expertise",
      "Gold Award winners in the Best Estate Agent Guide",
      "160 active listings across Southville, Bedminster, and Harbourside",
      "Dedicated Move Manager assigned to every client",
    ],
    faq: [
      {
        question: "What areas does Ocean Southville cover?",
        answer:
          "Ocean Southville covers Southville, Bedminster, Harbourside, and City Centre. They are the leading agent in the BS3 postcode, with particular expertise in one of Bristol's fastest-growing residential markets.",
      },
      {
        question: "What services does Ocean offer in Southville?",
        answer:
          "Ocean Southville provides residential sales, lettings, and property management. Every client is assigned a dedicated Move Manager to guide them through the process from start to finish.",
      },
      {
        question: "What are Ocean Southville's opening hours?",
        answer:
          "Ocean Southville is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "cj-hole-westbury",
    name: "CJ Hole",
    branch: "Westbury-on-Trym",
    address: "25 Canford Lane, Westbury-on-Trym",
    postcode: "BS9 3DQ",
    phone: "0117 950 0118",
    website: "https://www.cjhole.co.uk/estate-agents-and-letting-agents/branch/westbury-on-trym/",
    email: "westbury@cjhole.co.uk",
    description:
      "CJ Hole's Westbury-on-Trym office opened in 2014 as a joint venture serving the highly desirable BS9 postcode. They handle sales and lettings across Westbury-on-Trym, Henleaze, and Stoke Bishop, with the team holding MNAEA and ARLA qualifications and 75+ years of combined experience.",
    established: 2014,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Westbury-on-Trym", "Henleaze", "Stoke Bishop", "Sneyd Park"],
    propertyCount: 120,
    avgPrice: "£475,000",
    rating: 4.4,
    reviewCount: 267,
    hours: [
      { days: "Mon–Fri", time: "08:45–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "CJ Hole's Westbury-on-Trym office opened in 2014 to serve the highly desirable BS9 postcode, and it quickly established itself as a key player in one of Bristol's most family-friendly neighbourhoods. The team holds MNAEA and ARLA qualifications alongside 75 years of combined experience, bringing both professional rigour and genuine local insight.\n\nWith 120 active listings and an average price of £475,000, the branch sits right in the sweet spot of north Bristol's family market. They cover Westbury-on-Trym, Henleaze, Stoke Bishop, and Sneyd Park, all areas prized for their village feel, green spaces, and strong schools.\n\nAs part of the wider CJ Hole network (one of Bristol's oldest property brands, dating to 1867), this branch benefits from cross-referrals and a large buyer database while maintaining its own focused, neighbourhood-first identity.",
    whyChoose: [
      "MNAEA and ARLA qualified team with 75+ years of combined experience",
      "120 active listings across the desirable BS9 postcode",
      "Part of the CJ Hole network, Bristol's oldest estate agency brand",
      "Rated 4.4/5 from 267 client reviews",
    ],
    faq: [
      {
        question: "What areas does CJ Hole Westbury-on-Trym cover?",
        answer:
          "CJ Hole Westbury-on-Trym covers Westbury-on-Trym, Henleaze, Stoke Bishop, and Sneyd Park, all within the highly sought-after BS9 postcode.",
      },
      {
        question: "What services does CJ Hole offer in Westbury-on-Trym?",
        answer:
          "CJ Hole Westbury-on-Trym provides residential sales, lettings, and property management. Their team holds MNAEA and ARLA qualifications, ensuring professional standards across all services.",
      },
      {
        question: "What are CJ Hole Westbury-on-Trym's opening hours?",
        answer:
          "CJ Hole Westbury-on-Trym is open Monday to Friday from 08:45 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "allen-harris-clifton",
    name: "Allen & Harris",
    branch: "Clifton",
    address: "23 Regent Street, Clifton",
    postcode: "BS8 4HW",
    phone: "0117 973 1295",
    website: "https://www.allenandharris.co.uk/estate-agents/clifton-bristol",
    email: "clifton@allenandharris.co.uk",
    description:
      "Allen & Harris has been in the same prominent location on the end of Royal York Crescent for nearly 30 years. Part of the Sequence national network, they combine a large buyer database with vast local knowledge across Clifton, Redland, Cliftonwood, and Bristol City Centre. Their lettings department has over 50 years of combined experience.",
    established: 1946,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "New Homes"],
    areas: ["Clifton", "Clifton Village", "Redland", "Cotham", "Whiteladies Road", "Westbury-on-Trym"],
    propertyCount: 105,
    avgPrice: "£395,000",
    rating: 4.0,
    reviewCount: 298,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:00–14:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Allen & Harris has occupied the same prominent position at the end of Royal York Crescent for nearly 30 years, making their office one of the most visible in Clifton. The location is no accident: it places them at the crossroads of Clifton Village, Regent Street, and the approach from the Suspension Bridge.\n\nAs part of the Sequence national network, they combine one of the country's largest buyer databases with deep local knowledge. The lettings department alone has over 50 years of combined experience, and the sales team covers a broad patch from Clifton and Redland through Cotham and into Westbury-on-Trym. With 105 listings and an average price of £395,000, they cater to a wide range of budgets.\n\nFor sellers who want national reach without losing the local touch, Allen & Harris offers a pragmatic, well-resourced option backed by nearly 80 years of trading history.",
    whyChoose: [
      "Nearly 30 years in the same prominent Clifton location",
      "National buyer database through the Sequence network",
      "Lettings team with over 50 years of combined experience",
      "Full service: sales, lettings, property management, and new homes",
    ],
    faq: [
      {
        question: "What areas does Allen & Harris Clifton cover?",
        answer:
          "Allen & Harris Clifton covers Clifton, Clifton Village, Redland, Cotham, Whiteladies Road, and Westbury-on-Trym, with additional reach through the Sequence national network.",
      },
      {
        question: "What services does Allen & Harris offer in Clifton?",
        answer:
          "Allen & Harris Clifton provides residential sales, lettings, property management, and new homes services. Their lettings department has over 50 years of combined experience, and they benefit from one of the UK's largest buyer databases.",
      },
      {
        question: "What are Allen & Harris Clifton's opening hours?",
        answer:
          "Allen & Harris Clifton is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:00 to 14:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "ocean-bishopston",
    name: "Ocean",
    branch: "Bishopston",
    address: "201 Gloucester Road, Bishopston",
    postcode: "BS7 8BG",
    phone: "0117 942 5855",
    website: "https://www.oceanhome.co.uk/contact-us/bishopston-sales/",
    email: "bishopston@oceanhome.co.uk",
    description:
      "Ocean's Bishopston branch on Gloucester Road was the company's very first office, opened in 1983 by founder Paul Harris. With a philosophy of 'people before property', they cover Bishopston, St Andrews, Ashley Down, Horfield, Montpelier, and surrounding areas with a dedicated Move Manager for every client.",
    established: 1983,
    specialisms: ["Residential Sales", "Lettings", "Property Management"],
    areas: ["Bishopston", "Redland", "Cotham", "Henleaze", "Westbury-on-Trym"],
    propertyCount: 105,
    avgPrice: "£420,000",
    rating: 4.5,
    reviewCount: 324,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "This is where Ocean began. The Bishopston branch on Gloucester Road was the company's very first office, opened in 1983 by founder Paul Harris. That origin story matters: it means the team has been embedded in this community for over 40 years, longer than any competitor on the street.\n\nOcean Bishopston covers a vibrant patch of north Bristol, from the independent shops and cafes of Gloucester Road through to the family streets of Henleaze and Westbury-on-Trym. With 105 active listings, an average price of £420,000, and 324 client reviews, this is a branch that combines volume with genuine local credibility.\n\nEvery client gets a dedicated Move Manager, reflecting Ocean's 'people before property' philosophy. For sellers and landlords in BS7, the combination of heritage, reach, and personal service is compelling.",
    whyChoose: [
      "Ocean's original office, serving Bishopston since 1983",
      "Rated 4.5/5 from 324 client reviews",
      "Dedicated Move Manager for every client",
      "Deep roots in the Gloucester Road community for over 40 years",
    ],
    faq: [
      {
        question: "What areas does Ocean Bishopston cover?",
        answer:
          "Ocean Bishopston covers Bishopston, Redland, Cotham, Henleaze, and Westbury-on-Trym, with wider reach into St Andrews, Ashley Down, Horfield, and Montpelier through the Ocean branch network.",
      },
      {
        question: "What services does Ocean offer in Bishopston?",
        answer:
          "Ocean Bishopston provides residential sales, lettings, and property management. Every client is assigned a dedicated Move Manager, and the branch benefits from Ocean's city-wide network and large buyer database.",
      },
      {
        question: "What are Ocean Bishopston's opening hours?",
        answer:
          "Ocean Bishopston is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "romans-clifton",
    name: "Romans",
    branch: "Clifton Village",
    address: "21 Princess Victoria Street, Clifton Village",
    postcode: "BS8 4BX",
    phone: "0117 970 6119",
    website: "https://www.romans.co.uk/estate-lettings-agents/clifton",
    email: "clifton@romans.co.uk",
    description:
      "Romans is an established lettings and estate agency founded in 1987, with their Clifton Village branch in a prime position on Princess Victoria Street. They offer residential lettings, buy-to-let mortgages, property surveys, conveyancing, and investment advice, with particular strength in the lettings market.",
    established: 1987,
    specialisms: ["Residential Sales", "Lettings", "Property Management", "Relocation"],
    areas: ["Clifton Village", "Clifton", "Redland", "Cotham", "Whiteladies Road", "Harbourside"],
    propertyCount: 90,
    avgPrice: "£1,500/month",
    rating: 4.3,
    reviewCount: 186,
    hours: [
      { days: "Mon–Thu", time: "08:45–18:00" },
      { days: "Fri", time: "08:45–17:30" },
      { days: "Sat", time: "09:00–13:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Romans has carved out a distinctive position in the Clifton market since 1987, with particular strength in lettings. Their Princess Victoria Street office sits in the heart of Clifton Village, a location that reinforces their premium positioning and gives them direct access to one of Bristol's most active rental markets.\n\nWhile they handle residential sales, Romans really comes into its own for landlords and tenants. They offer buy-to-let mortgage advice, property surveys, conveyancing, and investment guidance alongside conventional lettings and property management. With 90 properties typically available and an average rental price of £1,500 per month, they operate at the higher end of the lettings market.\n\nTheir relocation service is a valuable addition for professionals and families moving to Bristol, providing a single point of contact for finding a home in an unfamiliar city.",
    whyChoose: [
      "Lettings specialists with particular strength in Clifton Village",
      "Full lettings support: buy-to-let mortgages, surveys, and conveyancing",
      "Relocation service for professionals and families moving to Bristol",
      "Rated 4.3/5 from 186 client reviews",
    ],
    faq: [
      {
        question: "What areas does Romans Clifton Village cover?",
        answer:
          "Romans Clifton Village covers Clifton Village, Clifton, Redland, Cotham, Whiteladies Road, and Harbourside. They are particularly well established in the BS8 rental market.",
      },
      {
        question: "What services does Romans offer in Clifton Village?",
        answer:
          "Romans provides residential sales, lettings, property management, and relocation services. They also offer buy-to-let mortgage advice, property surveys, conveyancing, and investment guidance for landlords.",
      },
      {
        question: "What are Romans Clifton Village's opening hours?",
        answer:
          "Romans Clifton Village is open Monday to Thursday from 08:45 to 18:00, Friday from 08:45 to 17:30, and Saturday from 09:00 to 13:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "richard-harding-clifton",
    name: "Richard Harding",
    branch: "Clifton",
    address: "124 Whiteladies Road, Clifton",
    postcode: "BS8 2RP",
    phone: "0117 946 6690",
    website: "https://www.richardharding.co.uk",
    email: "sales@richardharding.co.uk",
    description:
      "Richard Harding is an independent, privately-owned family estate agency and chartered surveyors on Whiteladies Road. With over 320 years of combined team experience, they specialise exclusively in sales (no lettings), allowing them to offer unbiased advice. Run by three partners, two of whom are Chartered Surveyors, they handle everything from studio apartments to manor houses.",
    established: 1998,
    specialisms: ["Residential Sales", "Land & Development"],
    areas: ["Clifton", "Clifton Village", "Redland", "Cotham", "Sneyd Park", "Stoke Bishop", "Leigh Woods"],
    propertyCount: 45,
    avgPrice: "£585,000",
    rating: 4.9,
    reviewCount: 112,
    hours: [
      { days: "Mon–Fri", time: "09:00–18:00" },
      { days: "Sat", time: "09:00–16:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Richard Harding stands apart from almost every other agent on this list for one reason: they do not offer lettings. By focusing exclusively on residential sales and land, they avoid the conflicts of interest that can arise when an agency manages both sides of the market. That clarity of purpose has earned them a 4.9-star rating from 112 reviews.\n\nThe firm is run by three partners, two of whom are Chartered Surveyors. Combined with the wider team, that adds up to over 320 years of collective property experience. They handle everything from studio apartments in Clifton to substantial country houses in Leigh Woods and Sneyd Park.\n\nEstablished in 1998 and privately owned, Richard Harding operates without the pressures of a corporate parent. The result is unhurried, expert advice driven by the client's best interests rather than internal targets.",
    whyChoose: [
      "Rated 4.9/5, one of Bristol's highest-rated estate agents",
      "Sales-only focus eliminates conflicts of interest",
      "Over 320 years of combined team experience",
      "Chartered Surveyors on the partnership, offering expert property insight",
    ],
    faq: [
      {
        question: "What areas does Richard Harding Clifton cover?",
        answer:
          "Richard Harding covers Clifton, Clifton Village, Redland, Cotham, Sneyd Park, Stoke Bishop, and Leigh Woods. They handle properties ranging from studio apartments to substantial country houses.",
      },
      {
        question: "What services does Richard Harding offer in Clifton?",
        answer:
          "Richard Harding specialises exclusively in residential sales and land and development. They do not offer lettings, which allows the team to provide unbiased advice focused entirely on achieving the best sale outcome.",
      },
      {
        question: "What are Richard Harding Clifton's opening hours?",
        answer:
          "Richard Harding Clifton is open Monday to Friday from 09:00 to 18:00 and Saturday from 09:00 to 16:00. They are closed on Sundays.",
      },
    ],
  },
  {
    slug: "hydes-clifton",
    name: "Hydes of Bristol",
    branch: "Clifton Village",
    address: "28 Princess Victoria Street, Clifton Village",
    postcode: "BS8 4BU",
    phone: "0117 973 1516",
    website: "https://www.hydes.co.uk",
    email: "post@hydes.co.uk",
    description:
      "Hydes of Bristol is a fiercely independent, award-winning estate agency established in 1988 in the heart of Clifton Village. With over three decades of impeccable experience, the partners personally conduct viewings rather than outsourcing, ensuring exceptional knowledge and continuity of service. They excel at fine period town houses and country residences.",
    established: 1988,
    specialisms: ["Residential Sales", "Land & Development"],
    areas: ["Clifton", "Clifton Village", "Redland", "Sneyd Park", "Stoke Bishop", "Leigh Woods", "Abbots Leigh"],
    propertyCount: 40,
    avgPrice: "£675,000",
    rating: 4.8,
    reviewCount: 96,
    hours: [
      { days: "Mon–Fri", time: "09:00–17:30" },
      { days: "Sat", time: "09:30–14:00" },
      { days: "Sun", time: "Closed" },
    ],
    aboutText:
      "Hydes of Bristol is the definition of a boutique estate agency. Established in 1988 on Princess Victoria Street, the firm has spent over three decades building a reputation for personal, partner-led service in Clifton Village and the surrounding premium areas. The partners personally conduct every viewing, a level of commitment that larger firms simply cannot match.\n\nWith just 40 carefully selected listings at any time and an average price of £675,000, this is an agency that prioritises quality over volume. They excel at fine period town houses and country residences, the kind of properties where presentation and local knowledge make a real difference to the outcome.\n\nHydes covers Clifton, Sneyd Park, Stoke Bishop, Leigh Woods, and Abbots Leigh. Their fiercely independent status means every decision is made in-house, with no corporate layer between client and adviser. A 4.8-star rating from 96 reviews reflects the consistency of that approach.",
    whyChoose: [
      "Partners personally conduct every viewing",
      "Rated 4.8/5 with over three decades of Clifton Village expertise",
      "Boutique portfolio: 40 select listings averaging £675,000",
      "Fiercely independent, with no corporate parent or franchise obligations",
    ],
    faq: [
      {
        question: "What areas does Hydes of Bristol Clifton Village cover?",
        answer:
          "Hydes of Bristol covers Clifton, Clifton Village, Redland, Sneyd Park, Stoke Bishop, Leigh Woods, and Abbots Leigh. They specialise in premium residential areas within and around the BS8 postcode.",
      },
      {
        question: "What services does Hydes of Bristol offer in Clifton Village?",
        answer:
          "Hydes of Bristol focuses on residential sales and land and development. Like Richard Harding, they do not offer lettings, concentrating their expertise entirely on helping clients buy and sell fine period and country properties.",
      },
      {
        question: "What are Hydes of Bristol Clifton Village's opening hours?",
        answer:
          "Hydes of Bristol is open Monday to Friday from 09:00 to 17:30 and Saturday from 09:30 to 14:00. They are closed on Sundays.",
      },
    ],
  },
]
